import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91制片厂',
    description: aboutData.introduction,
    url: 'https://www.91pianzhi.com/about',
    about: {
      '@type': 'Organization',
      name: '91制片厂',
      foundingDate: '2018',
      description: aboutData.introduction,
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pmkchn2trhb35gvi1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || parsed?.meta?.detail || responseText || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
          />

          {/* Hero Banner */}
          <section className="relative rounded-lg overflow-hidden mb-14 md:mb-20">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20film%20studio%20interior%20with%20warm%20atmospheric%20lighting%2C%20professional%20camera%20equipment%2C%20director%20chair%2C%20empty%20film%20set%20with%20dark%20curtains%2C%20gold%20and%20crimson%20tones%2C%20elegant%20professional%20environment&width=1440&height=450&seq=about-hero-01&orientation=landscape"
              alt="91制片厂 - 关于我们"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/90 via-background-50/60 to-background-50/30"></div>
            <div className="relative z-10 py-16 md:py-20 px-6 md:px-12">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 mb-4">
                关于<span className="text-primary-400">91制片厂</span>
              </h1>
              <p className="text-foreground-300 text-sm md:text-lg max-w-xl leading-relaxed">
                {aboutData.brandSlogan}
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="max-w-4xl mx-auto mb-14 md:mb-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
                品牌<span className="text-primary-400">故事</span>
              </h2>
              <p className="text-foreground-400 text-sm leading-relaxed max-w-2xl mx-auto">
                {aboutData.introduction}
              </p>
            </div>

            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/50 md:-translate-x-px"></div>
              <div className="space-y-8 md:space-y-12">
                {aboutData.story.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 md:gap-10 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:flex md:w-1/2"></div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1.5 md:-translate-x-1.5 mt-1.5 z-10 ring-4 ring-background-50"></div>
                    <div className="ml-10 md:ml-0 md:w-1/2 bg-background-100 border border-background-200/30 rounded-lg p-5">
                      <span className="inline-block px-3 py-1 text-xs font-bold text-primary-400 bg-primary-500/10 rounded-full mb-2">
                        {item.year}
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-50 mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-foreground-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="max-w-4xl mx-auto mb-14 md:mb-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
                核心<span className="text-primary-400">价值观</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutData.values.map((value) => (
                <div
                  key={value.title}
                  className="bg-background-100 border border-background-200/30 rounded-lg p-6"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-primary-500/10 rounded-lg mb-4">
                    <i className={`${value.icon} text-xl text-primary-400`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-50 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-foreground-400 text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-4xl mx-auto mb-14 md:mb-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
                行业<span className="text-primary-400">口碑</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutData.testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-100 border border-background-200/30 rounded-lg p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-50">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.title}</div>
                    </div>
                  </div>
                  <p className="text-foreground-300 text-sm leading-relaxed italic">
                    &ldquo;{t.content}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="max-w-2xl mx-auto mb-14 md:mb-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
                联系<span className="text-primary-400">我们</span>
              </h2>
              <p className="text-foreground-400 text-sm">
                有任何问题或合作意向，欢迎随时与我们联系
              </p>
            </div>

            <div className="bg-background-100 border border-background-200/30 rounded-lg p-6 md:p-8">
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-300 mb-1.5">
                      姓名 <span className="text-primary-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-50 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-300 mb-1.5">
                      邮箱 <span className="text-primary-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-50 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm text-foreground-300 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-50 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入咨询主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm text-foreground-300 mb-1.5">
                    留言内容 <span className="text-primary-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-50 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多可输入500个字符</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      发送留言
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 p-4 bg-accent-500/10 border border-accent-500/30 rounded-lg text-sm text-accent-400">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    感谢您的留言！我们会在1-2个工作日内与您联系。
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="flex items-center gap-2 p-4 bg-primary-500/10 border border-primary-500/30 rounded-lg text-sm text-primary-400">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
              <div className="text-center p-5 bg-background-100 border border-background-200/30 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-500/10 rounded-lg">
                  <i className="ri-phone-line text-lg text-primary-400"></i>
                </div>
                <div className="text-sm font-medium text-foreground-50 mb-1">电话咨询</div>
                <div className="text-xs text-foreground-400">{aboutData.contact.phone}</div>
              </div>
              <div className="text-center p-5 bg-background-100 border border-background-200/30 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-500/10 rounded-lg">
                  <i className="ri-mail-line text-lg text-primary-400"></i>
                </div>
                <div className="text-sm font-medium text-foreground-50 mb-1">邮件联系</div>
                <div className="text-xs text-foreground-400">{aboutData.contact.email}</div>
              </div>
              <div className="text-center p-5 bg-background-100 border border-background-200/30 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-500/10 rounded-lg">
                  <i className="ri-map-pin-line text-lg text-primary-400"></i>
                </div>
                <div className="text-sm font-medium text-foreground-50 mb-1">公司地址</div>
                <div className="text-xs text-foreground-400">{aboutData.contact.address}</div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}